import { useReducer, useCallback, useRef } from "react";
import { VoiceChatApiClient, captureBrowserContext } from "../lib/api-client";
import type {
  BootConfig,
  ConnectionInfo,
  SessionStartResponse,
  SessionState,
  SheetState,
} from "../types";

// ============================================================================
// State Types
// ============================================================================

interface ConversationState {
  sessionState: SessionState;
  sheetState: SheetState;
  /** The current conversation ID (persistent across reconnects) */
  conversationId: string | null;
  /** Room URL for WebRTC connection */
  dailyRoom: string | null;
  /** Room token for WebRTC connection */
  dailyToken: string | null;
  connectionInfo: ConnectionInfo | null;
  existingConversationId: string | null;
  existingConversationStatus: string | null;
  error: string | null;
  isLoading: boolean;
  hasAttemptedConnection: boolean;
}

type SessionAction =
  | { type: "START_BOOT" }
  | {
      type: "BOOT_SUCCESS";
      connectionInfo: ConnectionInfo;
      existingConversationId?: string;
      existingConversationStatus?: string;
    }
  | { type: "BOOT_ERROR"; error: string }
  | { type: "START_SESSION" }
  | {
      type: "SESSION_STARTED";
      conversationId: string;
      dailyRoom: string;
      dailyToken: string;
    }
  | { type: "SESSION_ERROR"; error: string }
  | { type: "CONNECTED" }
  | { type: "DISCONNECTED" }
  | { type: "PAUSED" }
  | { type: "COMPLETE" }
  | { type: "ABANDONED" }
  | { type: "INTERRUPTED" }
  | { type: "PREPARE_RECONNECT" }
  | { type: "RESET" };

const initialState: ConversationState = {
  sessionState: "idle",
  sheetState: "loading",
  conversationId: null,
  dailyRoom: null,
  dailyToken: null,
  connectionInfo: null,
  existingConversationId: null,
  existingConversationStatus: null,
  error: null,
  isLoading: false,
  hasAttemptedConnection: false,
};

// Compute sheet state from session state
function computeSheetState(state: ConversationState): SheetState {
  // Terminal states
  if (state.sessionState === "complete") return "complete";
  if (state.sessionState === "abandoned") return "abandoned";

  // Loading states
  if (state.sessionState === "idle" || state.sessionState === "booting") {
    return "loading";
  }

  // Paused by user
  if (state.sessionState === "paused") return "paused";

  // Interrupted unexpectedly
  if (state.sessionState === "interrupted") return "interrupted";

  // Active connection - hide sheet
  if (
    state.sessionState === "connected" ||
    state.sessionState === "connecting"
  ) {
    return "hidden";
  }

  // Error state - show interrupted
  if (state.sessionState === "error") return "interrupted";

  // Ready to start
  if (state.sessionState === "ready") {
    if (state.existingConversationStatus === "in_progress") return "resume";
    if (state.existingConversationStatus === "completed") return "complete";
    return "start";
  }

  return "start";
}

function sessionReducer(
  state: ConversationState,
  action: SessionAction,
): ConversationState {
  let newState: ConversationState;

  switch (action.type) {
    case "START_BOOT":
      newState = {
        ...state,
        sessionState: "booting",
        isLoading: true,
        error: null,
      };
      break;
    case "BOOT_SUCCESS":
      newState = {
        ...state,
        sessionState: "ready",
        connectionInfo: action.connectionInfo,
        existingConversationId: action.existingConversationId || null,
        existingConversationStatus: action.existingConversationStatus || null,
        conversationId: action.existingConversationId || null,
        isLoading: false,
        error: null,
      };
      break;
    case "BOOT_ERROR":
      newState = {
        ...state,
        sessionState: "error",
        error: action.error,
        isLoading: false,
      };
      break;
    case "START_SESSION":
      newState = {
        ...state,
        sessionState: "connecting",
        isLoading: true,
        hasAttemptedConnection: true,
      };
      break;
    case "SESSION_STARTED":
      newState = {
        ...state,
        sessionState: "connecting",
        conversationId: action.conversationId,
        dailyRoom: action.dailyRoom,
        dailyToken: action.dailyToken,
        isLoading: false,
      };
      break;
    case "SESSION_ERROR":
      newState = {
        ...state,
        sessionState: "error",
        error: action.error,
        isLoading: false,
      };
      break;
    case "CONNECTED":
      newState = { ...state, sessionState: "connected" };
      break;
    case "DISCONNECTED":
      newState = {
        ...state,
        sessionState: state.hasAttemptedConnection ? "interrupted" : "ready",
      };
      break;
    case "PAUSED":
      newState = { ...state, sessionState: "paused" };
      break;
    case "COMPLETE":
      newState = { ...state, sessionState: "complete" };
      break;
    case "ABANDONED":
      newState = { ...state, sessionState: "abandoned" };
      break;
    case "INTERRUPTED":
      newState = { ...state, sessionState: "interrupted" };
      break;
    case "PREPARE_RECONNECT":
      newState = {
        ...state,
        sessionState: "connecting",
        dailyRoom: null,
        dailyToken: null,
        error: null,
        isLoading: true,
      };
      break;
    case "RESET":
      return initialState;
    default:
      return state;
  }

  return { ...newState, sheetState: computeSheetState(newState) };
}

// ============================================================================
// Hook Options
// ============================================================================

export interface UseConversationOptions {
  /** Connection code for the conversation */
  connectionCode: string;
  /** API client instance (optional, creates one if not provided) */
  apiClient?: VoiceChatApiClient;
  /** Called when boot succeeds */
  onReady?: () => void;
  /** Called on errors */
  onError?: (error: string) => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for managing voice conversation state.
 * Works with both React and Preact (via preact/compat).
 *
 * Terminology:
 * - conversation: The overall interaction, can span multiple sessions
 * - session: A specific agent runner session (Pipecat) within a conversation
 *
 * Usage:
 * 1. Call boot() with config to initialize
 * 2. Call startConversation() when user clicks start (after mic permission)
 * 3. Use sessionState/sheetState to render UI
 * 4. Call setConnected/setDisconnected when pipecat connection changes
 */
export function useConversation({
  connectionCode,
  apiClient,
  onReady,
  onError,
}: UseConversationOptions) {
  const [state, dispatch] = useReducer(sessionReducer, initialState);
  const bootConfigRef = useRef<BootConfig | null>(null);
  const apiClientRef = useRef<VoiceChatApiClient>(
    apiClient || new VoiceChatApiClient(),
  );

  /**
   * Boot the session - validates connection code and fetches greeting data.
   * Also checks for existing conversations if a userToken is provided.
   */
  const boot = useCallback(
    async (config: BootConfig = {}) => {
      if (!connectionCode) {
        const error = "Connection code is required";
        dispatch({ type: "BOOT_ERROR", error });
        onError?.(error);
        return;
      }

      dispatch({ type: "START_BOOT" });
      bootConfigRef.current = config;

      try {
        const client = apiClientRef.current;

        // Set API URL if provided
        if (config.apiUrl) {
          client.setApiUrl(config.apiUrl);
        }

        // Set user token if provided
        if (config.userToken) {
          client.setToken(config.userToken);
        }

        // Fetch connection info
        const connectionInfo =
          await client.readConnectionByCode(connectionCode);

        // Check for existing conversations (requires token)
        let existingConversationId: string | undefined;
        let existingConversationStatus: string | undefined;

        if (config.userToken) {
          const conversations =
            await client.getUserConversations(connectionCode);
          const latest = conversations[0];
          if (latest) {
            const status = latest.status;

            const isInProgress = status === "in_progress";
            const isCompleted = status === "completed";

            const shouldSkip =
              (isInProgress && config.skipResumableConversations) ||
              (isCompleted && config.skipCompletedConversations);

            if (!shouldSkip) {
              existingConversationId = latest.id;
              existingConversationStatus = status;
            }
          }
        }

        dispatch({
          type: "BOOT_SUCCESS",
          connectionInfo,
          existingConversationId,
          existingConversationStatus,
        });
        onReady?.();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Failed to load connection";
        dispatch({ type: "BOOT_ERROR", error: message });
        onError?.(message);
      }
    },
    [connectionCode, onReady, onError],
  );

  /**
   * Start a new conversation or resume existing.
   * Call this AFTER mic permission and wake lock have been requested.
   */
  const startConversation = useCallback(async () => {
    if (!connectionCode) {
      const error = "Connection code is required";
      dispatch({ type: "SESSION_ERROR", error });
      onError?.(error);
      return null;
    }

    dispatch({ type: "START_SESSION" });

    try {
      const client = apiClientRef.current;
      const config = bootConfigRef.current || {};

      let result: SessionStartResponse;

      // Resume existing in-progress session
      if (
        state.existingConversationId &&
        state.existingConversationStatus === "in_progress"
      ) {
        result = await client.resumeSession(state.existingConversationId);
      } else {
        // Start new session
        const sessionProperties = {
          ...captureBrowserContext(),
          ...(config.sessionProperties || {}),
        };
        result = await client.startNewSession(
          connectionCode,
          sessionProperties,
          config.customContext,
        );
      }

      dispatch({
        type: "SESSION_STARTED",
        conversationId: result.conversationId,
        dailyRoom: result.transport.url || "",
        dailyToken: result.transport.token || "",
      });

      return result;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to start conversation";
      dispatch({ type: "SESSION_ERROR", error: message });
      onError?.(message);
      return null;
    }
  }, [
    connectionCode,
    onError,
    state.existingConversationId,
    state.existingConversationStatus,
  ]);

  /**
   * Resume an existing conversation (reconnect after disconnect)
   */
  const resumeConversation = useCallback(async () => {
    if (!state.conversationId) {
      return startConversation();
    }

    dispatch({ type: "START_SESSION" });

    try {
      const client = apiClientRef.current;
      const result = await client.resumeSession(state.conversationId);

      dispatch({
        type: "SESSION_STARTED",
        conversationId: result.conversationId,
        dailyRoom: result.transport.url || "",
        dailyToken: result.transport.token || "",
      });

      return result;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to resume conversation";
      dispatch({ type: "SESSION_ERROR", error: message });
      onError?.(message);
      return null;
    }
  }, [state.conversationId, startConversation, onError]);

  // Connection state setters
  const setConnected = useCallback(() => dispatch({ type: "CONNECTED" }), []);
  const setDisconnected = useCallback(
    () => dispatch({ type: "DISCONNECTED" }),
    [],
  );
  const setPaused = useCallback(() => dispatch({ type: "PAUSED" }), []);
  const setComplete = useCallback(() => dispatch({ type: "COMPLETE" }), []);
  const setAbandoned = useCallback(() => dispatch({ type: "ABANDONED" }), []);
  const setInterrupted = useCallback(
    () => dispatch({ type: "INTERRUPTED" }),
    [],
  );
  const prepareReconnect = useCallback(
    () => dispatch({ type: "PREPARE_RECONNECT" }),
    [],
  );
  const reset = useCallback(() => dispatch({ type: "RESET" }), []);

  return {
    // State
    ...state,
    // Actions
    boot,
    startConversation,
    resumeConversation,
    setConnected,
    setDisconnected,
    setPaused,
    setComplete,
    setAbandoned,
    setInterrupted,
    prepareReconnect,
    reset,
  };
}
